import { Toaster } from "sonner";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import React, { useEffect, useRef } from "react";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import { logError } from "./utils/errorLogging";
import {
  isTokenExpiredOrExpiring,
  refreshFirebaseToken,
  handleTokenTimeout,
} from "./utils/authUtils";

import AppRoutes from "./routes";

// Store
import { useStore } from "./store";
import { ME } from "./lib/urls";

function App() {
  const setUid = useStore((state) => state.setUid);
  const setUserLoading = useStore((state) => state.setUserLoading);
  const setToken = useStore((state) => state.setToken);
  const setCompany = useStore((state) => state.setCompany);
  const setUser = useStore((state) => state.setUser);
  const setOnboardingRequired = useStore(
    (state) => state.setOnboardingRequired
  );
  const setRole = useStore((state) => state.setRole);
  const uid = useStore((state) => state.uid);

  const tokenCheckInterval = useRef(null);
  const lastActivity = useRef(Date.now());

  const updateActivity = () => {
    lastActivity.current = Date.now();
  };

  const wasRecentlyActive = (withinMinutes = 30) => {
    const now = Date.now();
    const activityThreshold = withinMinutes * 60 * 1000; // Convert to milliseconds
    return now - lastActivity.current < activityThreshold;
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // This prevents firebase auth race condition as well as users
      // who haven't logged in from sending undefined tokens to backend
      if (user) {
        try {
          const token = await user.getIdToken();
          setUid(user.uid);
          setToken(token);
          await fetchMe(token);
        } catch (error) {
          console.error("Error fetching token:", error);
        }
        // Handle logged-out state
      } else {
        if (process.env.NODE_ENV === 'development' && module.hot) {
          console.log('HMR detected - preserving auth state');
          setUserLoading(false);
          return;
        }
        setUid(null);
        setToken(null);
        setUser(null);
        setCompany(null);
      }
      setUserLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Activity tracking setup
  useEffect(() => {
    if (uid) {
      // Track user activity events
      const activityEvents = [
        "mousedown",
        "mousemove",
        "keydown",
        "scroll",
        "touchstart",
      ];

      activityEvents.forEach((event) => {
        document.addEventListener(event, updateActivity, { passive: true });
      });

      return () => {
        // Clean up activity listeners
        activityEvents.forEach((event) => {
          document.removeEventListener(event, updateActivity);
        });
      };
    }
  }, [uid]);

  // Periodic token validation for logged-in users
  useEffect(() => {
    const checkTokenPeriodically = async () => {
      if (!uid) return; // Only check if user is logged in

      try {
        // Check if user has been inactive for too long (30 minutes)
        if (!wasRecentlyActive(30)) {
          // User inactive → logout
          await handleTokenTimeout();
        } else {
          // User active → manage token refresh
          const isExpiring = await isTokenExpiredOrExpiring(10); // Check if expires in 10 minutes

          if (isExpiring) {
            const refreshResult = await refreshFirebaseToken();

            if (refreshResult.success) {
              setToken(refreshResult.token);
            } else {
              await handleTokenTimeout();
            }
          }
        }
      } catch (error) {
        console.error("Error during periodic token check:", error);
        // Transient check failures should not force logout.
        // Let the next interval retry while keeping session state intact.
      }
    };

    if (uid) {
      // Start periodic checking when user is logged in - check every 5 minutes
      tokenCheckInterval.current = setInterval(checkTokenPeriodically, 5 * 60 * 1000); // Check every 5 minutes
    } else {
      // Clear interval when user is logged out
      if (tokenCheckInterval.current) {
        clearInterval(tokenCheckInterval.current);
        tokenCheckInterval.current = null;
      }
    }

    return () => {
      if (tokenCheckInterval.current) {
        clearInterval(tokenCheckInterval.current);
      }
    };
  }, [uid, setToken]);

  const fetchMe = async (token) => {
    try {
      const response = await fetch(ME, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const body = await response.json();
        const {
          user: { id, ...data },
          company,
          onboarding_required,
          role,
        } = body;

        setUser(data);
        setCompany(company ?? null);
        setOnboardingRequired(!!onboarding_required);
        setRole(role ?? null);
        return data;
      } else if (response.status === 401 || response.status === 403) {
        // Token expired and API call failed - handle timeout
        await handleTokenTimeout();
      }
    } catch (error) {
      console.error("Error fetching me:", error);
    }
  };

  return (
    <ErrorBoundary
      title="Application Error"
      description="The application encountered an unexpected problem. Please refresh the page."
      onError={(error, errorInfo) =>
        logError("app_level_error", error, errorInfo)
      }
    >
      <div>
        <Toaster richColors />
        <AppRoutes />
      </div>
    </ErrorBoundary>
  );
}

export default App;
